/**
 * Resolved catalog view (ADMIN-PR2-b addendum) —
 *   GET /v1/categories/{key}/resolved?site_region_key=
 *
 * Shows the effective merged catalog (stock + the caller-org's overrides) for a
 * category, with the generation token (body + `Catalog-Generation` header) and an
 * optional site/region tier.
 *
 * CONTRACT NOTE: the masters resolver takes NO target-org parameter — it resolves
 * for the authenticated session's org (from the token), not an arbitrary picked
 * org. So there is no org-picker here; only the site/region tier is selectable.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { getResolvedCatalog, type ResolvedCatalogResult } from "../auth/mastersApi";
import { errorMessage, useAsync } from "../lib/useAsync";
import { EmptyState, ErrorBanner, PageHeader, Spinner, StatusBadge } from "../components/ui";

function scopeOf(orgId: string | null): "stock" | "org" {
  return orgId === null ? "stock" : "org";
}

export function ResolvedCatalogPage(): ReactNode {
  const { key = "" } = useParams();
  const [regionInput, setRegionInput] = useState("");
  const [siteRegion, setSiteRegion] = useState("");

  const { data, loading, error } = useAsync<ResolvedCatalogResult>(
    () => getResolvedCatalog(key, siteRegion ? { site_region_key: siteRegion } : {}),
    [key, siteRegion],
  );

  function onApply(e: FormEvent) {
    e.preventDefault();
    setSiteRegion(regionInput.trim());
  }

  const generation = data?.generationHeader ?? data?.data.generation ?? null;

  return (
    <div className="space-y-4">
      <div>
        <Link to={`/catalog/${encodeURIComponent(key)}`} className="text-sm text-oxbo hover:underline">
          ← {key}
        </Link>
      </div>
      <PageHeader title={`Resolved catalog — ${key}`}>
        {generation && (
          <span className="rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-600">
            generation {generation}
          </span>
        )}
      </PageHeader>

      <p className="text-xs text-slate-500">
        Effective merged catalog for <strong>your organization</strong> (the masters resolver uses
        the session’s org — it has no target-org parameter), optionally narrowed to a site/region
        tier.
      </p>

      <form onSubmit={onApply} className="flex items-end gap-2">
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Site/region key (optional)</span>
          <input
            value={regionInput}
            onChange={(e) => setRegionInput(e.target.value)}
            placeholder="all tiers"
            className="w-64 rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
          />
        </label>
        <button
          type="submit"
          className="rounded bg-oxbo px-3 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark"
        >
          Resolve
        </button>
      </form>

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBanner message={errorMessage(error)} />
      ) : !data || data.data.entries.length === 0 ? (
        <EmptyState message="No effective entries for this category." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Key</th>
                <th className="px-3 py-2 font-medium">Label</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Region</th>
                <th className="px-3 py-2 font-medium">Properties</th>
              </tr>
            </thead>
            <tbody>
              {data.data.entries.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 last:border-0 align-top">
                  <td className="px-3 py-2 font-mono text-xs">{e.entry_key}</td>
                  <td className="px-3 py-2">{e.display_label}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={scopeOf(e.organization_id)} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">
                    {e.site_region_key ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    {e.properties ? (
                      <pre className="max-w-md overflow-x-auto rounded bg-slate-50 p-2 text-[11px] text-slate-700">
                        {JSON.stringify(e.properties, null, 2)}
                      </pre>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
